import { useState } from "react";
import PrimaryButton from "@/components/buttons/primary-button/PrimaryButton";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import Modal from "@/components/modal/Modal";
import { literals } from "@/constants/literals";
import useReceptionDataStore from "@/store/useReceptionData.store";
import { createGroup, createOrder } from "@/lib/services/order";
import { uploadImagesConcurrent } from "@/lib/services/upload-images";
import { appToast } from "@/lib/toast";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "@/constants/constants";
import { useLoader } from "@/store/useLoader";
import { Icon } from "@/components/icons/Icon";
import TerciaryButton from "@/components/buttons/terciary-button/TerciaryButton";
import DiscardOrderModal from "@/pages/capture/components/DiscardOrderModal";

const CloseReceptionOrderModal = ({
  onClose,
  viewModal,
  noGroupsInData,
}: {
  onClose: () => void;
  viewModal: boolean;
  noGroupsInData: boolean;
}) => {
  const { receptionData, receptionGroupData } = useReceptionDataStore();
  const navigate = useNavigate();
  const { upLoader, downLoader } = useLoader();
  const [confirmDiscardModal, setConfirmDiscardModal] = useState(false);

  const openConfirmDiscardModal = () => setConfirmDiscardModal(true);

  const closeModals = () => {
    setConfirmDiscardModal(false);
    onClose();
  };

  const closeConfirmDiscardModal = () => setConfirmDiscardModal(false);

  const saveOrder = async (goToCaptureExitPhotos?: boolean) => {
    if (noGroupsInData) return;
    const params: any = {
      area: literals.noServCell,
      step: receptionData.step,
      customer: receptionData.customer,
      originalCustomer: receptionData.originalCustomer,
      ownPackaging: receptionData.ownPackaging,
      correctPackaging: receptionData.correctPackaging,
      damagedPackaging: receptionData.damagedPackaging,
      packagingReference: receptionData.packagingReference
        ? receptionData.packagingReference
        : null,
    };

    if (receptionData.customerOrderNoserv) {
      params.customerOrderNoserv = receptionData.customerOrderNoserv;
    }

    if (receptionData.orderNumber) {
      params.sapWorkOrder = receptionData.orderNumber;
    }
    if (receptionData.serialIn) {
      params.serialIn = receptionData.serialIn;
    }

    if (receptionData.orderIn) {
      params.orderIn = receptionData.orderIn;
    }

    try {
      upLoader();
      const order = await createOrder(params);

      const groupsPromises = receptionGroupData.map((group) =>
        createGroup({
          orderId: order.id,
          description: group.description,
          visible: group.visibleForClient,
        }),
      );
      const groupsList = await Promise.all(groupsPromises);

      type PhotoItem = { file: File; imageGroupId: number; route?: string };
      const photosList: PhotoItem[] = [];

      for (let i = 0; i < receptionGroupData.length; i += 1) {
        const group = receptionGroupData[i];
        const createdGroupId = groupsList[i].id;

        group.photos.forEach((photo: any) => {
          const file: File =
            photo?.file instanceof File
              ? photo.file
              : photo instanceof File
                ? photo
                : undefined;
          if (!file) return;
          photosList.push({
            file,
            imageGroupId: createdGroupId,
            route: photo?.route || undefined,
          });
        });
      }

      // Buckets por (imageGroupId, route)
      type Bucket = { imageGroupId: number; route?: string; files: File[] };
      const bucketsMap = new Map<string, Bucket>();
      for (const p of photosList) {
        const key = `${p.imageGroupId}|${p.route || ""}`;
        const bucket = bucketsMap.get(key);
        if (bucket) {
          bucket.files.push(p.file);
        } else {
          bucketsMap.set(key, {
            imageGroupId: p.imageGroupId,
            route: p.route,
            files: [p.file],
          });
        }
      }
      const buckets = Array.from(bucketsMap.values());

      // Concurrencia de buckets y progreso
      const BUCKET_CONCURRENCY = 3;
      const FILES_CONCURRENCY_PER_BUCKET = 6;

      const runBucketsWithConcurrency = async <T,>(
        items: Bucket[],
        worker: (b: Bucket) => Promise<T>,
        concurrency: number,
      ): Promise<T[]> => {
        const results: T[] = [];
        let index = 0;
        const runners = new Array(Math.min(concurrency, items.length))
          .fill(0)
          .map(async () => {
            while (index < items.length) {
              const current = index++;
              results[current] = await worker(items[current]);
            }
          });
        await Promise.all(runners);
        return results;
      };

      // Mapa de progreso por bucket (key: imageGroupId)
      const progressByBucket = new Map<number, number>();

      const bucketResults = await runBucketsWithConcurrency(
        buckets,
        async (bucket) => {
          // Inicializa progreso a 0 por bucket
          progressByBucket.set(bucket.imageGroupId, 0);

          const res = await uploadImagesConcurrent(
            bucket.files,
            bucket.imageGroupId,
            bucket.route,
            {
              concurrency: FILES_CONCURRENCY_PER_BUCKET,
              onProgress: (done) => {
                // progreso por bucket
                progressByBucket.set(bucket.imageGroupId, done);
              },
            },
          );
          return { bucket, ...res };
        },
        BUCKET_CONCURRENCY,
      );

      // Resumen y cierre
      const totalOk = bucketResults.reduce(
        (acc, r) => acc + r.ok_files.length,
        0,
      );
      const totalKo = bucketResults.reduce(
        (acc, r) => acc + r.ko_files.length,
        0,
      );

      if (totalKo > 0) throw Error;

      downLoader();
      appToast.success(
        literals.correctUpload,
        totalOk === 1
          ? literals.savedOnePicture
          : `${literals.savedPicturesPart1} ${totalOk} ${literals.images}`,
      );
      if (goToCaptureExitPhotos) {
        navigate(`${BASE_URL}/capture/reception/`);
      } else {
        navigate(`${BASE_URL}/capture`);
      }
    } catch (error) {
      if (error) {
        downLoader();
        appToast.error(literals.aProblem, literals.uploadNoPossible);
      }
    }
  };

  const closeMainModal = () => {
    if (receptionData.step === literals.inNoServ) {
      return (
        <div className="w-full grid gap-4">
          <div>
            <p className="text-sm text-ib-pt-color-gray-1000">
              {literals.confirmText}
            </p>
          </div>
          <button
            type="button"
            className="flex group items-center flex-row w-full h-12 text-ib-pt-lg px-5 py-3 gap-x-3 justify-center rounded-[2px] font-ib-normal text-ib-pt-color-white bg-ib-pt-color-red-500 hover:bg-ib-pt-color-red-800"
            onClick={() => saveOrder(true)}
          >
            <div className="text-ib-pt-color-white">
              <Icon name="checkUnderline" className="size-4" />
            </div>
            <span className="text-nowrap">
              {literals.confirmAndContiue}{" "}
              <span className="font-ib-bold">{literals.exitReception}</span>
            </span>
          </button>
          <SecondaryButton
            colour="red"
            label={literals.confirmAndExit}
            onClick={() => saveOrder(false)}
          />
          <TerciaryButton
            colour="red"
            label={literals.discardOrder}
            onClick={openConfirmDiscardModal}
          />
        </div>
      );
    }
    return (
      <div className="w-full grid gap-4">
        <div>
          <p className="text-sm text-ib-pt-color-gray-1000">
            {literals.confirmText}
          </p>
        </div>
        <PrimaryButton
          colour="red"
          label={literals.confirmAndExit}
          onClick={() => saveOrder(false)}
        />
        <TerciaryButton
          colour="red"
          label={literals.discardOrder}
          onClick={openConfirmDiscardModal}
        />
      </div>
    );
  };

  return (
    <>
      {!confirmDiscardModal ? (
        <Modal
          title={literals.confirmation}
          opened={viewModal}
          onClose={onClose}
        >
          {closeMainModal()}
        </Modal>
      ) : (
        <DiscardOrderModal
          closeModalsGroup={closeModals}
          closeDiscardModal={closeConfirmDiscardModal}
        />
      )}
    </>
  );
};

export default CloseReceptionOrderModal;
